import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import type { BreachBriefConfig, BreachBriefRoute } from "../scenario/types";
import { audio } from "../lib/audio";
import { hubExerciseHref } from "../lib/hostConfig";
import { activeTtsLang } from "../lib/locale";
import { SkipButton } from "../ui/SkipButton";
import { useSim } from "../state/store";
import { tx } from "../lib/tx";

/**
 * Reception-terminal hub. Renders the same physical welcome-terminal chrome as
 * Terminal.tsx (`.terminal`/`.term-bezel`/`.term-screen--kiosk`, power-on
 * gate, narrated lines), then a header (kicker, title, one-line subtitle), a
 * lead row of two equal cards (Watch | Practise), optional further card rows
 * and a quiz bar under the scrolling body.
 *
 * While the lines are narrated the bottom bar shows the line being read: App
 * hides the global caption bar for this device kind (HAS_OWN_DIALOGUE_UI), so
 * the bar is the only on-screen copy of the voice.
 *
 * Deliberately a standalone component (NOT extra optional fields on
 * Terminal.tsx) for the same reason TerminalDebrief.tsx is: the shared kiosk
 * file that every other course renders never has to change shape for one
 * course's layout.
 */
export function BreachBrief({
  config,
  onExit,
  resumed = false,
}: {
  config: BreachBriefConfig;
  /** fires with the chosen exit's routing key, consumed by `edges[].on`. */
  onExit: (key: string) => void;
  /** Returning from a spoke: skip power-on + narration and show the hub now. */
  resumed?: boolean;
}) {
  const visitedBeatIds = useSim((s) => s.visitedBeatIds);
  const [on, setOn] = useState(resumed);
  const [shown, setShown] = useState(resumed ? config.lines.length : 0);
  // `shown` counts lines STARTED; the quiz bar waits until the last one has
  // finished, so the closing line is captioned while it plays.
  const [linesDone, setLinesDone] = useState(resumed);
  const [moreBelow, setMoreBelow] = useState<string | null>(null);
  const started = useRef(false);
  const alive = useRef(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  const voiceOpts = { voice: config.voiceId ?? "onyx", lang: config.language ?? activeTtsLang() };

  useEffect(() => {
    alive.current = true;
    if (!resumed) {
      config.lines.forEach((l) => void audio.prefetchSpeech(l, voiceOpts));
    }
    return () => {
      alive.current = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!on || resumed || started.current) return;
    started.current = true;
    let i = 0;
    const next = async () => {
      if (!alive.current) return;
      if (!config.lines[i]) {
        setLinesDone(true);
        return;
      }
      setShown(i + 1);
      // Pipeline the NEXT line's fetch while this one plays, so there is no
      // silent gap between lines.
      if (config.lines[i + 1]) void audio.prefetchSpeech(config.lines[i + 1], voiceOpts);
      await audio.speak(config.lines[i], voiceOpts);
      i += 1;
      if (alive.current) void next();
    };
    void next();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  useEffect(() => {
    const body = bodyRef.current;
    if (!on || !body) return;
    const update = () => {
      if (body.scrollHeight - body.scrollTop - body.clientHeight <= 4) {
        setMoreBelow(null);
        return;
      }
      const fold = body.getBoundingClientRect().bottom - 24;
      const next = [...body.querySelectorAll<HTMLElement>(".bbf-section-head h2")].find(
        (heading) => heading.getBoundingClientRect().top > fold,
      );
      setMoreBelow(next?.textContent || "More below");
    };
    update();
    body.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(body);
    if (body.firstElementChild) observer.observe(body.firstElementChild);
    return () => {
      body.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [on]);

  const stopNarration = () => {
    alive.current = false;
    audio.cancelSpeech();
  };
  const skip = () => {
    stopNarration();
    setShown(config.lines.length);
    setLinesDone(true);
  };
  const scrollMore = () => {
    const body = bodyRef.current;
    if (body) body.scrollBy({ top: body.clientHeight * 0.8, behavior: "smooth" });
  };

  const storageKey = config.progressKey ? `cf-hub-done:${config.progressKey}` : "";
  const readDone = () => {
    if (!storageKey) return new Set<string>();
    try {
      const raw = JSON.parse(localStorage.getItem(storageKey) || "[]");
      return new Set<string>(Array.isArray(raw) ? raw.filter((x) => typeof x === "string") : []);
    } catch {
      return new Set<string>();
    }
  };
  const [savedDone, setSavedDone] = useState<Set<string>>(readDone);
  const markDone = (id: string) => {
    if (!storageKey) return;
    const next = readDone();
    next.add(id);
    localStorage.setItem(storageKey, JSON.stringify([...next]));
    setSavedDone(new Set(next));
  };
  const isComplete = (route: BreachBriefRoute) =>
    savedDone.has(route.id) || visitedBeatIds.has(route.completionBeatId);
  const decksDone = [...config.cards, ...config.exercises].every(isComplete);
  const launch = (route: BreachBriefRoute) => {
    stopNarration();
    markDone(route.id);
    if (route.href) {
      window.location.href = hubExerciseHref(route.href);
      return;
    }
    onExit(route.key);
  };

  const video = config.hubVideo;
  const watched = savedDone.has("video");
  const allDone = decksDone && (!video?.src || watched);
  const openFilm = () => {
    // The film has its own soundtrack; never let the kiosk voice run under it.
    if (!linesDone) skip();
    markDone("video");
  };
  // A hub that already has lesson cards puts the film on its own row above
  // those cards. Hubs with no lesson cards keep Watch beside the first exercise.
  const filmAboveLessons = Boolean(video?.src && config.videoFirst && config.cards.length > 0);
  const leadExercise = video && !filmAboveLessons ? config.exercises[0] : undefined;
  const sectionExercises = video && !filmAboveLessons ? config.exercises.slice(1) : config.exercises;

  const cardSection = config.cards.length > 0 && (
    <HubSection title={config.cardsTitle ?? tx("Understand the risk")} intro={config.cardsIntro ?? tx("Two short lessons. Five slides each.")}>
      <div className={gridClass(config.cards)}>
        {config.cards.map((route) => (
          <RouteCard
            key={route.id}
            route={route}
            completed={isComplete(route)}
            onLaunch={() => launch(route)}
            action={route.actionLabel ?? (route.href ? "Start" : "View slides")}
            again={route.href ? tx("Practise again") : tx("View again")}
            showHint
          />
        ))}
      </div>
    </HubSection>
  );
  const exerciseSection = sectionExercises.length > 0 && (
    <HubSection title={config.exercisesLabel ?? tx("Practise")} pill={config.exercisesKicker}>
      <div className={gridClass(sectionExercises)}>
        {sectionExercises.map((route) => (
          <RouteCard
            key={route.id}
            route={route}
            completed={isComplete(route)}
            onLaunch={() => launch(route)}
            action={route.actionLabel ?? "Practise"}
            again={tx("Practise again")}
          />
        ))}
      </div>
    </HubSection>
  );

  return (
    <div className="terminal bbf-host">
      <div className="term-bezel">
        <span className="term-bezel-cam" aria-hidden />
        {!on ? (
          <button
            type="button"
            className={`screen-power term-power-inset${config.idleImage ? " has-art" : ""}`}
            style={config.idleImage ? { backgroundImage: `url(${config.idleImage})` } : undefined}
            onClick={() => {
              audio.powerOn();
              setOn(true);
            }}
          >
            <span className={config.idleImage ? "term-power-chip" : undefined}>
            <span className="screen-power-ico" aria-hidden>
              <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M12 3v8" strokeLinecap="round" />
                <path d="M6.3 6.3a8 8 0 1 0 11.4 0" strokeLinecap="round" />
              </svg>
            </span>
            <span className="screen-power-label">{config.offLabel ?? tx("Switch on the welcome terminal")}</span>
            </span>
          </button>
        ) : (
          <div className="term-screen term-screen--kiosk bbf-screen">
            <header className="bbf-head">
              <span className="bbf-kicker">{config.noticeKicker ?? tx("Security awareness")}</span>
              {config.title && <h1 className="bbf-title">{config.title}</h1>}
              {config.hubIntro && <p className="bbf-sub">{config.hubIntro}</p>}
            </header>

            <div className={`bbf-body${moreBelow ? " has-more" : ""}`} ref={bodyRef}>
              <div className="bbf-stack">
                {filmAboveLessons && video && (
                  <div className="bbf-row bbf-row--film">
                    <WatchCard video={video} watched={watched} onOpen={openFilm} />
                  </div>
                )}
                {filmAboveLessons && cardSection}
                {video && !filmAboveLessons && (
                  <div className="bbf-row">
                    <WatchCard video={video} watched={watched} onOpen={openFilm} />
                    {leadExercise && (
                      <RouteCard
                        route={leadExercise}
                        completed={isComplete(leadExercise)}
                        onLaunch={() => launch(leadExercise)}
                        action={leadExercise.actionLabel ?? "Practise"}
                        again={tx("Practise again")}
                        heading={config.exercisesLabel ?? tx("Practise")}
                        pill={tx("Interactive exercise")}
                      />
                    )}
                  </div>
                )}
                {filmAboveLessons ? exerciseSection : video ? exerciseSection : cardSection}
                {filmAboveLessons ? null : video ? cardSection : exerciseSection}
              </div>
            </div>
            {moreBelow && (
              <button type="button" className="bbf-more" onClick={scrollMore}>
                {moreBelow}
                <ChevronGlyph />
              </button>
            )}

            {!linesDone ? (
              <div className="term-cta-row bbf-cta">
                <div className="bbf-bar">
                  <p className="bbf-bar-caption" aria-live="polite">
                    <span className="bbf-voice" aria-hidden>
                      <i />
                      <i />
                      <i />
                    </span>
                    <span>{config.lines[Math.max(0, shown - 1)]}</span>
                  </p>
                  <SkipButton onClick={skip} />
                </div>
              </div>
            ) : !config.hideFinish ? (
              <div className="term-cta-row bbf-cta">
                <div className="bbf-bar">
                  <div className="bbf-bar-text">
                    <strong>{tx("Ready for the final step?")}</strong>
                    <span>
                      {allDone
                        ? tx("Complete the quiz to finish this briefing.")
                        : config.finishEarlyHint ?? tx("You can take the quiz now, or explore the briefing first.")}
                    </span>
                  </div>
                  <button type="button" className="bbf-quiz" onClick={() => onExit("finish")}>
                    {tx("Continue to quiz")}
                    <ArrowGlyph />
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function gridClass(routes: BreachBriefRoute[]) {
  const n = routes.filter((route) => !route.wide).length;
  return `bbf-grid bbf-grid--${n <= 2 ? 2 : n === 4 ? 4 : 3}`;
}

function HubSection({
  title,
  intro,
  pill,
  children,
}: {
  title: string;
  intro?: string;
  pill?: string;
  children: ReactNode;
}) {
  return (
    <section className="bbf-section">
      <div className="bbf-section-head">
        <div>
          <h2>{title}</h2>
          {intro ? <p>{intro}</p> : null}
        </div>
        {pill ? <span className="bbf-pill">{pill}</span> : null}
      </div>
      {children}
    </section>
  );
}

function CardFoot({
  done,
  doneLabel,
  label,
  onClick,
}: {
  done: boolean;
  doneLabel: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <div className="bbf-card-foot">
      {done && (
        <span className="bbf-status">
          <CheckBadge />
          {doneLabel}
        </span>
      )}
      <button type="button" className={`bbf-btn ${done ? "bbf-btn--ghost" : "bbf-btn--primary"}`} onClick={onClick}>
        {label}
      </button>
    </div>
  );
}

function WatchCard({
  video,
  watched,
  onOpen,
}: {
  video: NonNullable<BreachBriefConfig["hubVideo"]>;
  watched: boolean;
  onOpen: () => void;
}) {
  const player = useRef<HTMLVideoElement>(null);
  const [open, setOpen] = useState(false);
  const close = () => {
    player.current?.pause();
    setOpen(false);
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    void player.current?.play();
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const openFilm = () => {
    onOpen();
    setOpen(true);
  };
  return (
    <article className={`bbf-card${watched ? " is-done" : ""}`}>
      <div className="bbf-card-head">
        <h2 className="bbf-card-heading">{tx("Watch")}</h2>
        <span className="bbf-pill">{tx("Short film")}</span>
      </div>
      {video.src ? (
        <button type="button" className="bbf-media" onClick={openFilm} tabIndex={-1} aria-label={tx("Play {title}", { title: video.title })}>
          {video.poster && <img src={video.poster} alt="" />}
          <span className="bbf-play" aria-hidden>
            <PlayGlyph />
          </span>
        </button>
      ) : (
        <div className="bbf-media bbf-media--wait">
          <span>{tx("The film will appear here.")}</span>
        </div>
      )}
      <div className="bbf-card-main">
        <div className="bbf-card-text">
          <h3 className="bbf-card-title">{video.title}</h3>
          {video.blurb && <p className="bbf-card-blurb">{video.blurb}</p>}
        </div>
        {video.src && (
          <CardFoot done={watched} doneLabel={tx("Watched")} label={watched ? tx("Watch again") : tx("Watch")} onClick={openFilm} />
        )}
      </div>
      {open &&
        createPortal(
          <div className="bbf-film" role="dialog" aria-modal="true" aria-label={video.title}>
            <button type="button" className="bbf-film-backdrop" aria-label={tx("Close the film")} onClick={close} />
            <div className="bbf-film-card">
              <div className="bbf-film-bar">
                <strong>{video.title}</strong>
                <button type="button" className="bbf-film-close" onClick={close}>
                  {tx("Close")}
                </button>
              </div>
              <video ref={player} src={video.src} poster={video.poster} controls autoPlay playsInline />
            </div>
          </div>,
          document.body,
        )}
    </article>
  );
}

function RouteCard({
  route,
  completed,
  onLaunch,
  action,
  again,
  heading,
  pill,
  showHint = false,
}: {
  route: BreachBriefRoute;
  completed: boolean;
  onLaunch: () => void;
  action: string;
  again: string;
  /** Lead-row cards carry a column heading and a type pill above the image. */
  heading?: string;
  pill?: string;
  showHint?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const label = completed ? again : action;
  return (
    <article className={`bbf-card${route.wide ? " bbf-card--wide" : ""}${completed ? " is-done" : ""}`}>
      {heading && (
        <div className="bbf-card-head">
          <h2 className="bbf-card-heading">{heading}</h2>
          {pill && <span className="bbf-pill">{pill}</span>}
        </div>
      )}
      <button type="button" className="bbf-media" onClick={onLaunch} tabIndex={-1} aria-label={`${label}: ${route.label}`}>
        {route.imageUrl && !failed ? (
          <img src={route.imageUrl} alt="" onError={() => setFailed(true)} />
        ) : (
          <span className="bbf-media-fallback" aria-hidden>
            <RouteGlyph icon={route.icon ?? "slides"} />
          </span>
        )}
        {showHint && route.hint ? <span className="bbf-media-tag">{route.hint}</span> : null}
      </button>
      <div className="bbf-card-main">
        <div className="bbf-card-text">
          <h3 className="bbf-card-title">{route.label}</h3>
          {route.blurb && <p className="bbf-card-blurb">{route.blurb}</p>}
        </div>
        <CardFoot done={completed} doneLabel={tx("Completed")} label={label} onClick={onLaunch} />
      </div>
    </article>
  );
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <path d="M9 6.6v10.8c0 .8.9 1.3 1.6.9l8.3-5.4c.6-.4.6-1.4 0-1.8L10.6 5.7C9.9 5.3 9 5.8 9 6.6Z" fill="currentColor" />
    </svg>
  );
}

function CheckBadge() {
  return (
    <svg className="bbf-check" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="10" fill="currentColor" />
      <path d="m5.8 10.4 2.8 2.8 5.6-5.9" fill="none" stroke="#04291e" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="m5.5 8 4.5 4.5L14.5 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="M3.5 10h12m-4.8-4.8L15.5 10l-4.8 4.8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RouteGlyph({ icon }: { icon: NonNullable<BreachBriefRoute["icon"]> }) {
  if (icon === "phishing") {
    return (
      <svg viewBox="0 0 32 32" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="4" y="7" width="24" height="18" rx="2.5" />
        <path d="m5 9 11 8 11-8M22 4c3 1 4 3 4 6" strokeLinecap="round" />
      </svg>
    );
  }
  if (icon === "download") {
    return (
      <svg viewBox="0 0 32 32" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M16 4v15m-6-5 6 6 6-6M6 24h20v4H6z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (icon === "wifi") {
    return (
      <svg viewBox="0 0 32 32" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 12c7-6 17-6 24 0M8 17c5-4 11-4 16 0M12 22c3-2 5-2 8 0" strokeLinecap="round" />
        <circle cx="16" cy="27" r="1.4" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (icon === "privacy") {
    return (
      <svg viewBox="0 0 32 32" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M6 14.5V10a10 10 0 0 1 20 0v4.5" strokeLinecap="round" />
        <rect x="5" y="14" width="22" height="13" rx="2.5" />
        <path d="M16 19.5v4" strokeLinecap="round" />
      </svg>
    );
  }
  if (icon === "chapters") {
    return (
      <svg viewBox="0 0 32 32" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="7" y="8" width="18" height="14" rx="2" />
        <path d="M5 12h2M5 16h2M25 12h2M25 16h2M11 26h10" strokeLinecap="round" />
      </svg>
    );
  }
  if (icon === "usb") {
    return (
      <svg viewBox="0 0 32 32" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M16 27V7m0 0-4 4m4-4 4 4M16 18l7-4m0 0v-4m0 4h4M16 22l-6-3m0 0v-4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="10" cy="15" r="1.5" /><rect x="14.5" y="3" width="3" height="3" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 32 32" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="5" y="6" width="22" height="18" rx="2.5" />
      <path d="M9 11h14M9 15h9M9 19h12M12 28h8" strokeLinecap="round" />
    </svg>
  );
}
